/**
 * Admin Model-Capability API Service
 *
 * Client for the dynamic LLM model-capability registry admin endpoints.
 * Aligned with backend routes at serverless/api/routes/admin/model_capability.py
 * (/api/v1/admin/model-capability).
 */

import { buildCloudApiUrl } from './urlResolver';
import { handleResponse, getAuthHeaders } from '../utils/httpUtils';

const ENDPOINTS = {
  base: '/api/v1/admin/model-capability',
  override: '/api/v1/admin/model-capability/override',
} as const;

export type CapabilityOverride = 'force_capable' | 'force_incapable' | null;

export interface CapabilityRow {
  model: string;
  generation_type: string;
  statically_capable: boolean;
  is_capable: boolean;
  attempts: number;
  successes: number;
  schema_failures: number;
  malformed: number;
  rate_limits: number;
  network_failures: number;
  schema_failure_rate: number;
  manual_override: CapabilityOverride;
}

export interface CapabilityTypeBlock {
  gated: boolean;
  rows: CapabilityRow[];
}

export interface ModelCapabilityReport {
  min_samples: number;
  schema_failure_rate_threshold: number;
  store_available: boolean;
  generation_types: Record<string, CapabilityTypeBlock>;
}

export interface OverrideResult {
  ok: boolean;
  error?: string;
  generation_type?: string;
  model?: string;
  override?: CapabilityOverride;
  is_capable?: boolean;
}

/** Fetch capability aggregates + live verdict per (generation_type, model). */
export async function fetchModelCapability(
  token: string,
  generationType?: string
): Promise<ModelCapabilityReport> {
  let url = buildCloudApiUrl(ENDPOINTS.base);
  if (generationType) {
    url += `?generation_type=${encodeURIComponent(generationType)}`;
  }
  const response = await fetch(url, { method: 'GET', headers: getAuthHeaders(token) });
  return handleResponse<ModelCapabilityReport>(response);
}

/** Pin a model capable/incapable (or clear) for a generation_type. */
export async function setCapabilityOverride(
  token: string,
  generationType: string,
  model: string,
  override: CapabilityOverride
): Promise<OverrideResult> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.override), {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ generation_type: generationType, model, override }),
  });
  return handleResponse<OverrideResult>(response);
}
